import React, { useState, useEffect, useMemo } from 'react';
import { Clock, FlaskConical, CheckCircle2, Sparkles, ArrowRight, Play, Check, ShieldCheck, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';
import { logActivity } from '@/utils/activityLogger';

export default function MaserasiTimer({ order, small = false, onTimerComplete }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isCompleted, setIsCompleted] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [isFinishing, setIsFinishing] = useState(false);

  const maserasiData = useMemo(() => {
    if (order?.status !== 'maserasi' || !order?.maserasi_start) return null;
    const start = new Date(order.maserasi_start).getTime();
    const daysMs = (order.maserasi_durasi_hari || 0) * 24 * 60 * 60 * 1000;
    const hoursMs = (order.maserasi_durasi_jam || 0) * 60 * 60 * 1000;
    const totalMs = Math.max(1000, daysMs + hoursMs);
    const target = start + totalMs;

    return { start, totalMs, target };
  }, [order?.status, order?.maserasi_start, order?.maserasi_durasi_hari, order?.maserasi_durasi_jam]);

  useEffect(() => {
    if (!maserasiData) return;

    const calculate = () => {
      const now = Date.now();
      const distance = maserasiData.target - now;
      const elapsed = Math.max(0, now - maserasiData.start);
      const pct = Math.min(100, Math.max(0, Math.round((elapsed / maserasiData.totalMs) * 100)));
      setProgressPct(pct);

      if (distance <= 0) {
        setIsCompleted(true);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        if (onTimerComplete) {
          onTimerComplete();
        }
        return true;
      }

      const d = Math.floor(distance / (1000 * 60 * 60 * 24));
      const h = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const m = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const s = Math.floor((distance % (1000 * 60)) / 1000);

      setIsCompleted(false);
      setTimeLeft({ days: d, hours: h, minutes: m, seconds: s });
      return false;
    };

    const done = calculate();
    if (done) return;

    const interval = setInterval(() => {
      if (calculate()) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [maserasiData, onTimerComplete]);

  // Handle manual force-complete by supervisor / production head
  const handleForceComplete = async () => {
    if (!order?.id || isFinishing) return;
    const confirmMsg = `Konfirmasi penyelesaian maserasi untuk PO #${order.order_number || order.id} (${order.brand_name || 'Brand'})?\n\nRacikan akan ditandai matang dan alur produksi dilanjutkan ke tahap Filling.`;
    if (!window.confirm(confirmMsg)) return;

    setIsFinishing(true);
    try {
      // Log activity
      await logActivity({
        portal: 'production',
        page: 'tasks',
        action_type: 'status_update',
        title: `Maserasi Selesai: ${order.brand_name || order.order_number}`,
        details: `Maserasi diselesaikan manual oleh supervisor. Status beralih ke tahap filling.`,
        target_id: order.id,
        target_name: order.brand_name || order.order_number,
      });

      if (onTimerComplete) {
        await onTimerComplete();
      }
      toast.success('Maserasi berhasil diselesaikan! Tahap Filling siap dijalankan.');
    } catch (err) {
      console.error('Gagal menyelesaikan maserasi:', err);
      toast.error('Gagal memperbarui status maserasi.');
    } finally {
      setIsFinishing(false);
    }
  };

  if (!maserasiData) return null;

  // 1. Compact View (untuk BrandCard di dashboard / list card / tugas)
  if (small) {
    if (isCompleted) {
      return (
        <div className="flex items-center gap-1.5 text-[9px] md:text-[10px] font-black px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-800 border-emerald-300 shadow-2xs">
          <Sparkles className="w-2.5 h-2.5 md:w-3 md:h-3 text-emerald-600 animate-bounce" />
          <span>Maserasi Matang</span>
        </div>
      );
    }

    const shortStr = timeLeft.days > 0 
      ? `${timeLeft.days}h ${timeLeft.hours}j` 
      : `${timeLeft.hours}j ${timeLeft.minutes}m`;

    return (
      <div className="flex items-center gap-1.5 text-[9px] md:text-[10px] font-bold px-2 py-0.5 rounded-full border bg-amber-500/10 text-[#8C6208] border-amber-500/20 shadow-2xs">
        <FlaskConical className="w-2.5 h-2.5 md:w-3 md:h-3 text-[#8C6208] animate-pulse" />
        <span>Sisa: {shortStr}</span>
      </div>
    );
  }

  // 2. Full Aesthetic Luxury View (untuk halaman ProductionDetail.jsx)
  const startDateStr = new Date(maserasiData.start).toLocaleDateString('id-ID', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
  const targetDateStr = new Date(maserasiData.target).toLocaleDateString('id-ID', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  return (
    <div className="relative overflow-hidden mt-6 mb-6 rounded-3xl border border-[#E5D7B7] bg-gradient-to-br from-[#FFFDF9] via-[#FAF6EE] to-[#F5EFE0] p-5 md:p-7 shadow-sm transition-all duration-300">
      {/* Decorative ambient gold glow background */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gradient-to-br from-[#D4AF37]/15 to-[#8C6208]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#8C6208]/5 blur-3xl" />

      {/* Header Bar */}
      <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4 pb-5 border-b border-[#E8DFC8]">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 md:w-13 md:h-13 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#8C6208] flex items-center justify-center text-white shadow-md shadow-amber-900/15 shrink-0">
            <FlaskConical className="w-5 h-5 md:w-6 md:h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display font-black text-lg md:text-xl text-[#302621] tracking-tight">
                Masa Maserasi Formula Parfum
              </h3>
              {isCompleted ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] md:text-xs font-black bg-emerald-100 text-emerald-800 border border-emerald-300 shadow-2xs">
                  <Sparkles className="w-3 h-3 text-emerald-600" />
                  Maserasi Matang (Selesai)
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] md:text-xs font-black bg-amber-100/90 text-amber-900 border border-amber-300/80 shadow-2xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-600 animate-ping" />
                  Sedang Mengendap
                </span>
              )}
            </div>
            <p className="text-xs text-stone-600 mt-0.5 font-medium">
              Proses pemeraman dan pengikatan aroma konsentrat agar stabil, bulat, dan tahan lama sebelum dikemas.
            </p>
          </div>
        </div>

        {/* Action Button: Selesaikan Lebih Awal atau Lanjut ke Filling */}
        <div className="flex items-center gap-2 self-start md:self-auto">
          {isCompleted ? (
            <Button
              onClick={handleForceComplete}
              disabled={isFinishing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs md:text-sm rounded-xl px-4 shadow-sm transition-all"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              Lanjut ke Tahap Filling
            </Button>
          ) : (
            <Button
              onClick={handleForceComplete}
              disabled={isFinishing}
              variant="outline"
              className="border-[#D4AF37]/60 bg-white/80 hover:bg-amber-50/80 text-[#8C6208] font-bold text-xs rounded-xl px-3.5 shadow-2xs"
            >
              <Zap className="w-3.5 h-3.5 mr-1.5 text-[#D4AF37]" />
              Selesaikan Lebih Awal
            </Button>
          )}
        </div>
      </div>

      {/* Main Countdown & Progress Section */}
      <div className="relative z-10 py-5">
        {isCompleted ? (
          <div className="bg-emerald-50/90 border border-emerald-200/90 rounded-2xl p-4 md:p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700 shrink-0">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm md:text-base font-bold text-emerald-950">
                Formula Parfum Telah Matang Sempurna!
              </h4>
              <p className="text-xs text-emerald-800 mt-0.5">
                Waktu maserasi telah terpenuhi ({order.maserasi_durasi_hari || 0} hari {order.maserasi_durasi_jam || 0} jam). Racikan siap masuk ke tahap pengisian botol (Filling).
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-6">
            {/* 4 Segmented Digital Countdown Cards */}
            <div className="flex items-center justify-center sm:justify-start gap-2.5 md:gap-3.5">
              {[
                { val: timeLeft.days, label: 'Hari' },
                { val: timeLeft.hours, label: 'Jam' },
                { val: timeLeft.minutes, label: 'Menit' },
                { val: timeLeft.seconds, label: 'Detik' },
              ].map((item, idx) => (
                <div 
                  key={item.label}
                  className="bg-white/90 backdrop-blur-xs border border-[#E8DFC8] rounded-2xl p-3 md:p-4 text-center shadow-xs min-w-[64px] sm:min-w-[74px] md:min-w-[82px]"
                >
                  <div className="text-xl sm:text-2xl md:text-3xl font-black font-mono text-[#302621] leading-none">
                    {String(item.val).padStart(2, '0')}
                  </div>
                  <div className="text-[9px] md:text-[10px] font-bold text-amber-900/70 uppercase tracking-wider mt-1">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Progress Bar & Percentage */}
            <div className="flex-1 lg:max-w-md flex flex-col justify-center">
              <div className="flex items-center justify-between text-xs font-bold text-[#4A3B2C] mb-1.5">
                <span>Pematangan Aroma</span>
                <span className="text-[#8C6208]">{progressPct}%</span>
              </div>
              <div className="h-2.5 w-full bg-[#E8DFC8]/60 rounded-full overflow-hidden p-0.5 border border-[#E2D5B7]">
                <div 
                  className="h-full bg-gradient-to-r from-[#D4AF37] to-[#8C6208] rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-[10px] text-stone-600 mt-1.5 font-medium">
                <span>Mulai: {startDateStr}</span>
                <span>Target: {targetDateStr}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Metadata Info Footer */}
      <div className="relative z-10 pt-4 border-t border-[#E8DFC8] flex flex-wrap items-center justify-between gap-3 text-xs text-stone-600">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-[#8C6208]" />
            Durasi Total: <strong className="text-slate-800 font-bold">{order.maserasi_durasi_hari || 0} Hari {order.maserasi_durasi_jam || 0} Jam</strong>
          </span>
          {order.maserasi_lokasi && (
            <span className="flex items-center gap-1.5">
              Ruang/Bejana: <strong className="text-slate-800 font-bold">{order.maserasi_lokasi}</strong>
            </span>
          )}
        </div>
        <div className="text-[11px] text-amber-900/60 font-medium">
          Satala Dermatech Quality Control Standard
        </div>
      </div>
    </div>
  );
}
